import {ChangeDetectionStrategy, Component, signal, TemplateRef, ViewChild} from '@angular/core';
import {Router, RouterLink, RouterLinkActive, RouterOutlet} from "@angular/router";
import {concat, map, Observable, of, startWith, switchMap, timer} from "rxjs";
import {AsyncPipe, JsonPipe} from "@angular/common";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {
    AngLiebSharedBadgeComponent,
    AngLiebSharedBodyComponent,
    AngLiebSharedDialogService,
    AngLiebSharedDialogStackComponent,
    AngLiebSharedIconComponent,
    AngLiebSharedMarginalComponent
} from "@liebink/ang-lieb-shared";
import {AngLiebCoreAuthenticatedService, AngLiebCoreErrorService, AngLiebCoreHttpService} from "@liebink/ang-lieb-core";
import {AngLiebServicePlatformContactService} from "@liebink/ang-lieb-service";

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'liebink-control',
    imports: [
        RouterOutlet,
        AsyncPipe,
        RouterLink,
        TranslatePipe,
        RouterLinkActive,
        AngLiebSharedDialogStackComponent,
        AngLiebSharedIconComponent,
        AngLiebSharedMarginalComponent,
        AngLiebSharedBodyComponent,
        JsonPipe,
        AngLiebSharedBadgeComponent,
    ],
    template: `
        <ang-lieb-shared-dialog-stack/>
        <ng-template #errorDialog let-error="error">
            <ang-lieb-shared-marginal>
                {{ 'shared.noun.error' | translate }}
            </ang-lieb-shared-marginal>
            <ang-lieb-shared-body>
                <pre>{{ error |json }}</pre>
            </ang-lieb-shared-body>
        </ng-template>
        <div class="layout-base">
            <header>
                <a href="/">
                    LiebinkControl
                </a>
                @if (isAuthenticated$ | async) {
                    <button
                            (click)="menuActive.set(!menuActive())"
                            class="toggle-menu link"
                            type="button"
                    >
                        {{ 'shared.noun.menu' | translate }}
                    </button>
                }
                <div class="bar">
                    <div class="process">
                        @switch (connectionState$ | async) {
                            @case ('processed') {
                                <ang-lieb-shared-icon
                                        class="tone-success"
                                        name="check"
                                />
                            }
                            @case ('processing') {
                                <ang-lieb-shared-icon
                                        class="tone-info spinning"
                                        name="processing"
                                />
                            }
                        }
                    </div>
                </div>
            </header>
            @if (isAuthenticated$ | async) {
                <nav
                        [class]="{active: menuActive()}"
                        class="menu-bar"
                >
                    <div class="menu-group">
                        <h3>
                            {{ 'shared.noun.product' | translate }}
                        </h3>
                        <div class="link-group">
                            <a
                                    routerLinkActive="active"
                                    [routerLink]="['/product/book']"
                            >
                                <ang-lieb-shared-icon name="books"/>
                                {{ 'shared.noun.books' | translate }}
                            </a>
                            <a
                                    routerLinkActive="active"
                                    [routerLink]="['/product/collection']"
                            >
                                <ang-lieb-shared-icon name="collection"/>
                                {{ 'shared.noun.collections' | translate }}
                            </a>
                            <a
                                    routerLinkActive="active"
                                    [routerLink]="['/product/contributor']"
                            >
                                <ang-lieb-shared-icon name="users"/>
                                {{ 'shared.noun.contributors' | translate }}
                            </a>
                            <a
                                    routerLinkActive="active"
                                    [routerLink]="['/product/publisher']"
                            >
                                <ang-lieb-shared-icon name="publisher"/>
                                {{ 'shared.noun.publishers' | translate }}
                            </a>
                            <a
                                    routerLinkActive="active"
                                    [routerLink]="['/product/imprint']"
                            >
                                <ang-lieb-shared-icon name="imprint"/>
                                {{ 'shared.noun.imprints' | translate }}
                            </a>
                            <a
                                    routerLinkActive="active"
                                    [routerLink]="['/product/supplier']"
                            >
                                <ang-lieb-shared-icon name="supplier"/>
                                {{ 'control.noun.suppliers' | translate }}
                            </a>
                            <a
                                    routerLinkActive="active"
                                    [routerLink]="['/product/topic']"
                            >
                                <ang-lieb-shared-icon name="tags"/>
                                {{ 'control.noun.topics' | translate }}
                            </a>
                        </div>
                    </div>
                    <div class="menu-group">
                        <h3>
                            {{ 'control.noun.platform' | translate }}
                        </h3>
                        <div class="link-group">
                            <a
                                    [routerLink]="['/platform/blog']"
                                    routerLinkActive="active"
                            >
                                <ang-lieb-shared-icon name="blog"/>
                                {{  'control.noun.blog' | translate }}
                            </a>
                            <a
                                    [routerLink]="['/platform/contact']"
                                    routerLinkActive="active"
                            >
                                <ang-lieb-shared-icon name="question"/>
                                {{  'shared.noun.contact' | translate }}

                                @if (openContactForms$ | async; as openContactForms) {
                                    <ang-lieb-shared-badge class="ml-auto tone-warning">
                                        {{ openContactForms }}
                                    </ang-lieb-shared-badge>
                                } @else {
                                    <ang-lieb-shared-badge class="ml-auto faded">
                                        0
                                    </ang-lieb-shared-badge>
                                }
                            </a>
                            <a
                                    [routerLink]="['/platform/catalog']"
                                    routerLinkActive="active"
                            >
                                <ang-lieb-shared-icon name="ul"/>
                                {{  'control.noun.catalog' | translate }}
                            </a>
                        </div>
                    </div>
                    <div class="menu-group hover-group">
                        <h3>
                            {{ 'shared.noun.mailer' | translate }}
                        </h3>
                        <div class="link-group">
                            <a
                                    [routerLink]="['/mailer/template']"
                                    routerLinkActive="active"
                            >
                                <ang-lieb-shared-icon name="code"/>
                                {{ 'shared.noun.templates' | translate }}
                            </a>
                            <a
                                    [routerLink]="['/mailer/layout']"
                                    routerLinkActive="active"
                            >
                                <ang-lieb-shared-icon name="file"/>
                                {{ 'shared.noun.layouts' | translate }}
                            </a>
                            <a
                                    [routerLink]="['/mailer/sender']"
                                    routerLinkActive="active"
                            >
                                <ang-lieb-shared-icon name="at"/>
                                {{ 'shared.noun.senders' | translate }}
                            </a>
                        </div>
                    </div>
                    <div class="menu-group hover-group">
                        <h3>
                            {{ 'shared.noun.identity' | translate }}
                        </h3>
                        <div class="link-group">
                            <a
                                    routerLinkActive="active"
                                    [routerLink]="['/identity/account']"
                            >
                                <ang-lieb-shared-icon name="user-key"/>
                                {{ 'shared.noun.accounts' | translate }}
                            </a>
                        </div>
                    </div>
                    <div class="menu-group hover-group">
                        <h3>
                            {{ 'shared.noun.support' | translate }}
                        </h3>
                        <div class="link-group">
                            <a
                                    routerLinkActive="active"
                                    [routerLink]="['/support/queue']"
                            >
                                <ang-lieb-shared-icon name="link"/>
                                Queue
                            </a>
                        </div>
                    </div>
                    <div class="menu-group hover-group">
                        <h3>
                            {{ 'shared.noun.account' | translate }}
                        </h3>
                        <div class="link-group">
                            <a [routerLink]="'/me/sign-out'">
                                <ang-lieb-shared-icon name="sign-out"/>
                                {{ 'shared.verb.signOut' | translate }}
                            </a>
                        </div>
                    </div>
                </nav>
            }
            <main>
                <router-outlet/>
            </main>
        </div>
    `,
})
export class AppComponent {
    @ViewChild('errorDialog') errorDialog: TemplateRef<any>;

    isAuthenticated$: Observable<boolean>;

    connectionState$: Observable<string | null>;

    openContactForms$: Observable<number>;

    menuActive = signal(false);

    constructor(
        authenticateService: AngLiebCoreAuthenticatedService,
        contactService: AngLiebServicePlatformContactService,
        dialogService: AngLiebSharedDialogService,
        errorService: AngLiebCoreErrorService,
        httpService: AngLiebCoreHttpService,
        translateService: TranslateService,
        router: Router,
    ) {
        this.isAuthenticated$ = authenticateService.isAuthenticated$();
        this.connectionState$ = httpService
            .hasActiveConnections$()
            .pipe(
                switchMap((active) => {
                    if (active) {
                        return of('processing');
                    }

                    return concat(
                        of('processed'),
                        timer(1000).pipe(map(() => null)),
                    );
                }),
            );

        this.openContactForms$ = contactService
            .changes()
            .pipe(
                startWith(null),
                switchMap(() => {
                    return contactService
                        .getBySubmitted(
                            { perPage: 1, page: 1 },
                            {
                                state: {
                                    mode: 'any',
                                    selected: ['open']
                                },
                            }
                        )
                }),
                map((response) => response.meta.total),
            );

        errorService
            .get()
            .subscribe((error) => {
                if (error === null) {
                    return;
                }

                dialogService
                    .open(
                        this.errorDialog,
                        { error: error },
                    );
            })

        dialogService
            .changes()
            .subscribe(() => {
                errorService.clear();
            })

        router
            .events
            .subscribe(() => {
                this.menuActive.set(false);
                dialogService.closeAll();
            })

        translateService.setFallbackLang('nl_NL');
        translateService.use('nl_NL');
    }
}
